import React, { useState, useEffect } from 'react';
import Ayoashy from '../assets/bmb-pc.jpeg';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaGithub, FaLinkedin, FaTwitter, FaDownload, FaArrowRight } from 'react-icons/fa';
import { HiOutlineMail } from 'react-icons/hi';

const roles = [
  'Frontend Engineer',
  'Fullstack Developer',
  'Web3 Builder',
  'React Specialist',
];

const useTypewriter = (words, speed = 90, pauseDuration = 2200) => {
  const [text, setText] = useState('');
  const [wordIndex, setWordIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPausing, setIsPausing] = useState(false);

  useEffect(() => {
    if (isPausing) return;
    const currentWord = words[wordIndex];

    const timeout = setTimeout(() => {
      if (!isDeleting) {
        if (text.length < currentWord.length) {
          setText(currentWord.substring(0, text.length + 1));
        } else {
          setIsPausing(true);
          setTimeout(() => {
            setIsPausing(false);
            setIsDeleting(true);
          }, pauseDuration);
        }
      } else {
        if (text.length > 0) {
          setText(currentWord.substring(0, text.length - 1));
        } else {
          setIsDeleting(false);
          setWordIndex((prev) => (prev + 1) % words.length);
        }
      }
    }, isDeleting ? speed / 2 : speed);

    return () => clearTimeout(timeout);
  }, [text, isDeleting, isPausing, wordIndex, words, speed, pauseDuration]);

  return text;
};

const stats = [
  { value: '4+', label: 'Years Experience' },
  { value: '10+', label: 'Projects Built' },
  { value: '4', label: 'Articles Written' },
  { value: '3+', label: 'Companies' },
];

const socials = [
  { href: 'https://www.linkedin.com/in/ayoashy/', icon: FaLinkedin, label: 'LinkedIn' },
  { href: 'https://github.com/ayoashy', icon: FaGithub, label: 'GitHub' },
  { href: 'mailto:ashiruayo@gmail.com', icon: HiOutlineMail, label: 'Email' },
  { href: 'https://twitter.com/topboyshoks', icon: FaTwitter, label: 'Twitter' },
];

const BriefIntroduction = () => {
  const typedText = useTypewriter(roles);

  return (
    <section className="relative bg-[#050505] min-h-screen flex items-center overflow-hidden dot-grid">
      {/* Gradient orbs */}
      <div className="absolute top-1/4 right-1/3 w-[500px] h-[500px] bg-[#4d760c] opacity-[0.04] rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 left-1/4 w-[300px] h-[300px] bg-[#4d760c] opacity-[0.06] rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-6xl mx-auto px-6 w-full pt-28 pb-16">
        <div className="flex flex-col-reverse md:flex-row items-center justify-between gap-14 md:gap-8">

          {/* ── Text Column ─────────────────────────── */}
          <motion.div
            className="flex-1 flex flex-col items-start"
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            {/* Greeting */}
            <motion.p
              className="text-[#4d760c] font-mono text-sm tracking-widest mb-3"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              &gt;_ Hello, world! I'm
            </motion.p>

            {/* Name */}
            <motion.h1
              className="text-5xl md:text-7xl font-bold text-white mb-2 leading-tight tracking-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.6 }}
            >
              Ayo{' '}
              <span className="gradient-text">Ashiru</span>
            </motion.h1>

            {/* Typewriter role */}
            <motion.div
              className="flex items-center h-10 mb-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.55 }}
            >
              <span className="text-xl md:text-2xl text-slate-400 font-mono">
                <span className="text-[#6ea60f]">{typedText}</span>
                <span className="cursor-blink" />
              </span>
            </motion.div>

            {/* Tagline */}
            <motion.p
              className="text-slate-500 max-w-md mb-9 leading-relaxed text-sm"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              Building scalable web applications and blockchain solutions.
              Passionate about clean code, great UX, and pushing the web forward.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              className="flex flex-wrap gap-4 mb-10"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85 }}
            >
              <a
                href="https://drive.google.com/file/d/1LAf_5DUYjtOvyKZVW6RwowWRVgVW_jHv/view?usp=sharing"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                <FaDownload size={13} />
                Download CV
              </a>
              <Link to="/contact" className="btn-outline">
                Get in Touch
                <FaArrowRight size={12} />
              </Link>
            </motion.div>

            {/* Social Icons */}
            <motion.div
              className="flex items-center gap-5"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
            >
              {socials.map(({ href, icon: Icon, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="text-slate-600 hover:text-[#6ea60f] transition-colors duration-300"
                >
                  <Icon size={20} />
                </a>
              ))}
            </motion.div>
          </motion.div>

          {/* ── Image Column ─────────────────────────── */}
          <motion.div
            className="flex-shrink-0 flex justify-center"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
          >
            <div className="relative w-56 h-56 md:w-72 md:h-72">
              {/* Outer glow */}
              <div className="absolute inset-0 rounded-full bg-[#4d760c] opacity-[0.15] blur-2xl scale-110 animate-pulse-glow" />
              {/* Spinning dashed ring */}
              <div className="absolute inset-0 rounded-full border-2 border-dashed border-[#4d760c35] animate-spin-slow" />
              {/* Inner static ring */}
              <div className="absolute inset-3 rounded-full border border-[#4d760c25]" />
              {/* Photo */}
              <img
                src={Ayoashy}
                alt="Ayo Ashiru"
                className="absolute inset-6 rounded-full object-cover border-2 border-[#4d760c50]"
                style={{ width: 'calc(100% - 48px)', height: 'calc(100% - 48px)' }}
              />
              {/* Floating code badge */}
              <motion.div
                className="absolute -bottom-3 -right-3 bg-[#0e0e0e] border border-[#1e1e1e] rounded-lg px-3 py-2 text-xs font-mono text-[#6ea60f] shadow-xl"
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
              >
                &lt;/fullstack&gt;
              </motion.div>
              {/* Top-left badge */}
              <motion.div
                className="absolute -top-2 -left-4 bg-[#0e0e0e] border border-[#1e1e1e] rounded-lg px-3 py-2 text-xs font-mono text-[#4d760c] shadow-xl"
                animate={{ y: [0, 5, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
              >
                const ayo = 🚀
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* ── Stats Bar ─────────────────────────────── */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 pt-10 border-t border-[#1a1a1a]"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.6 }}
        >
          {stats.map(({ value, label }, i) => (
            <motion.div
              key={label}
              className="text-center group"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2 + i * 0.1 }}
            >
              <p className="text-3xl md:text-4xl font-bold text-[#4d760c] group-hover:text-[#6ea60f] transition-colors duration-300 mb-1">
                {value}
              </p>
              <p className="text-xs text-slate-600 uppercase tracking-widest font-mono">
                {label}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
      >
        <span className="text-slate-700 font-mono text-xs tracking-widest">scroll</span>
        <motion.div
          className="w-px h-8 bg-gradient-to-b from-[#4d760c] to-transparent"
          animate={{ scaleY: [0, 1, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
        />
      </motion.div>
    </section>
  );
};

export default BriefIntroduction;
